import React, { useMemo } from 'react'
import { FolderORM } from 'src/types/types'

import {
  DNAButton,
  DNACard,
  DNABox,
  DNAText,
  DNACheckbox,
  Iffy,
  DNAIcon,
} from '@alucio/lux-ui'
import { DNAPopover, DNAFolderPinButton } from 'src/components/DNA'
import { useAppSettings } from 'src/state/context/AppSettings'
import { GRID_LIST_STYLES_VARIANTS_CONFIGS } from 'src/screens/MyFolders/constants'
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'

type DNANestedFolderRowProps = {
  folderORM: FolderORM,
  isChecked?: boolean,
  isCheckedDisabled?: boolean,
  disabledMessage?: string,
  onExpand?: () => void,
  isExpanded?: boolean,
  onPress?: () => void
}

const DNANestedFolderRow: React.FC<DNANestedFolderRowProps> = ({
  folderORM,
  isChecked,
  isExpanded,
  disabledMessage,
  isCheckedDisabled,
  onPress,
  onExpand,
}) => {
  const { deviceMode } = useAppSettings()
  const handleItemSelection = () => !isCheckedDisabled && onPress?.()
  const iconColor = useMemo(() => colors['color-ui2-brand-200'], []);

  const showPopover = disabledMessage && isCheckedDisabled;
  const checkbox = (
    <DNACheckbox
      checked={isChecked}
      disabled={isCheckedDisabled}
      onChange={handleItemSelection}
      testID="add-to-folder-checkbox"
    />
  )

  return (
    <DNACard
      appearance="outline"
      interactive={!isCheckedDisabled ? 'pressable' : undefined}
      onPress={handleItemSelection}
      style={{ padding: 12 }}
    >
      <DNABox testID="add-folder-container" fill childFill={0} alignY="center" spacing="between">
        <DNABox fill childFill={3} alignY="center" spacing="md">
          {/* Expand Chevron */}
          <Iffy is={folderORM.meta.folderCount}>
            <DNAButton
              appearance="ghost"
              iconLeft={isExpanded ? 'caret-up' : 'caret-right'}
              onPress={onExpand}
            />
          </Iffy>
          {/* Chevron Placeholder */}
          <Iffy is={!folderORM.meta.folderCount}>
            <DNABox style={{ width: 32, height: 32 }} />
          </Iffy>
          {/* Folder Icon & Title */}
          <DNAIcon.Styled
            name="folder-simple-fill"
            color={iconColor}
            width={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_ICON[deviceMode].width}
            height={GRID_LIST_STYLES_VARIANTS_CONFIGS.FOLDER_ICON[deviceMode].height}
          />
          <DNAText testID="add-to-folders-title-text" numberOfLines={2} style={{ flex: 1 }}>
            {folderORM.model.name}
          </DNAText>
        </DNABox>

        {/* Right side controls */}
        <DNABox alignY="center" spacing="md">
          <DNAFolderPinButton
            isPinned={folderORM.meta.isFavorite}
            isHidden={!folderORM.meta.isFavorite}
            interactive={false}
          />
          {
            showPopover
              ? <DNAPopover placement="top">
                <DNAPopover.Anchor>
                  {checkbox}
                </DNAPopover.Anchor>
                <DNAPopover.Content>
                  <DNAText colorMode="dark">{disabledMessage}</DNAText>
                </DNAPopover.Content>
              </DNAPopover> : checkbox
          }
        </DNABox>
      </DNABox>
    </DNACard>
  )
}

export default DNANestedFolderRow
